import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Box, Divider, Grid2, Typography } from "@mui/material";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { decryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import MultiSelectDropdown from "../Overview/components/MultiSelectDropdown";
import SeverityStatistics from "../BmsFaults/SeverityStatistics";
import CustomAlertListItem from "./BmsFaults/CustomAlertListItem";
import AlertListHeader from "../BmsFaults/AlertListHeader";
import { Pagination } from "antd";
import { getDiagnosticsBatteriesList } from "../../Api/BmsFaultsApi";
import CartLoader from "../../Helper/Loaders/CartLoader";
import AlertActionDialog from "../BmsFaults/AlertActionDialog";
import SelectionClearWarningDialog from "../BmsFaults/Components/SelectionClearWarningDialog";
import { getFilterData } from "../../Api/Api";
import OperationalStatusMultiSelect from "../Common/OperationalStatusMultiSelect";
import AlertSystemTabs from "../BmsFaults/AlertSystemTabs";
import SingleSelectDropdown from "../Overview/components/SingleSelectDropdown";
import { getDateRangeFromTimePeriod } from "../BmsFaults/CommonFunctions";
import useGlobalFilterData from "../../Hooks/useGlobalFilterData";

const timePeriodOptions = [
  "Today",
  "Yesterday",
  "Last 7 days",
];

// Maps config tab slugs to the legacy status strings expected by sub-components
const TAB_API_STATUS = {
  activeFaults: "active",
  resolvedFaults: "resolved",
};

const TAB_DISPLAY_LABEL = {
  activeFaults: "Active",
  resolvedFaults: "Resolved",
};

function CustomBMSAlertV2Main({ customConfig, activeTab, onTabChange }) {
  const queryParams = useMemo(() => decryptTheParams(), []);

  const {
    selectedFleets,
    allOnboardingLocations,
    globalFilters,
    setGlobalFilters,
    fetchFleets,
    fetchOnboardingLocations,
  } = useGlobalFilterData();

  const [selectedTab, setSelectedTab] = useState(activeTab);

  useEffect(() => {
    if (activeTab) setSelectedTab(activeTab);
  }, [activeTab]);
  const [alertCategories, setAlertCategories] = useState([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(
    activeTab === "resolvedFaults" ? timePeriodOptions[0] : null
  );
  const [allDefaultFiltersApplied, setAllDefaultFiltersApplied] = useState(false);
  const [allGlobalFiltersLoaded, setAllGlobalFiltersLoaded] = useState(false);

  const [expandAll, setExpandAll] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [isManualSelectAll, setIsManualSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBatteries, setSelectedBatteries] = useState(new Set());
  const [selectedAlertIncidents, setSelectedAlertIncidents] = useState(new Set());
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [actionDialogData, setActionDialogData] = useState({});
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [batteryList, setBatteryList] = useState([]);
  const [paginationData, setPaginationData] = useState({
    rowsPerPage: 10,
    currentPage: 1,
    total: 1,
  });
  const [searchTerm, setSearchTerm] = useState({
    selectedDevices: [],
    selectedSearchColumn: null,
  });
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedSort, setSelectedSort] = useState([]);
  const [defaultSort, setDefaultSort] = useState([]);

  const incidentsCacheRef = useRef({});
  const fetchRequestRef = useRef(0);

  const tabsToShow = customConfig.tabsToShow || ["activeFaults"];
  const activeTabConfig = customConfig[selectedTab] || {};
  const globalFilterConfig = activeTabConfig.globalFilters || {};
  const sections = activeTabConfig.sections || [];
  const searchableFields = activeTabConfig.lists?.faultList?.searchableFields || [];
  const showStatistics = sections.includes("statistics");

  // Legacy status string for sub-components that expect "active" / "resolved"
  const legacyTabStatus = TAB_API_STATUS[selectedTab] || "active";

  const tabs = tabsToShow.map((slug) => ({
    slug,
    name: TAB_DISPLAY_LABEL[slug] || slug,
    color: ThemeProperties.purple,
  }));

  const fetchAlertCategories = async () => {
    const filterConfig = {
      type: "dynamic",
      queryParams: { table: "diagnosticsMetaBOP", column: "category" },
    };
    const res = await getFilterData(filterConfig);
    const list = res?.response?.response?.filterData || [];
    setAlertCategories(list.map((c) => c.id));
  };

  const confirmSelectionClear = (action) => {
    if (selectedBatteries.size || selectAll) {
      setPendingAction(() => action);
      setIsWarningOpen(true);
    } else {
      action();
    }
  };

  const clearSelection = () => {
    setSelectedBatteries(new Set());
    setSelectedAlertIncidents(new Set());
    setIsManualSelectAll(false);
    setSelectAll(false);
    setPendingAction(null);
  };

  const handleAlertCategorySelection = (selectedOptions) => {
    confirmSelectionClear(() =>
      setGlobalFilters((prev) => ({ ...prev, alertCategories: selectedOptions }))
    );
  };

  const handleDepotSelection = (selectedOptions) => {
    confirmSelectionClear(() =>
      setGlobalFilters((prev) => ({
        ...prev,
        onboardingLocations: { ...prev.onboardingLocations, depots: selectedOptions },
      }))
    );
  };

  const handleOperationalStatusSelection = (selectedValues) => {
    confirmSelectionClear(() =>
      setGlobalFilters((prev) => ({ ...prev, operationalStatus: selectedValues }))
    );
  };

  const handleTimePeriodSelection = (option) => {
    confirmSelectionClear(() => setSelectedTimePeriod(option));
  };

  const handleSearch = (selectedDevices, selectedSearchColumn) => {
    confirmSelectionClear(() => {
      setSearchTerm({ selectedDevices, selectedSearchColumn });
      setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    });
  };

  const handleFilters = (filters) => {
    confirmSelectionClear(() => {
      setSelectedFilters(filters);
      setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    });
  };

  const handleSortChange = (updatedSort) => {
    confirmSelectionClear(() => {
      setSelectedSort([{ sortBy: updatedSort.key, order: updatedSort.order }]);
      setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    });
  };

  const handlePageChange = (page) => {
    confirmSelectionClear(() =>
      setPaginationData((prev) => ({ ...prev, currentPage: page }))
    );
  };

  const handleTabSwitch = (tabSlug) => {
    confirmSelectionClear(() => {
      setSelectedTab(tabSlug);
      onTabChange(tabSlug);
      if (tabSlug === "resolvedFaults" && !selectedTimePeriod) {
        setSelectedTimePeriod(timePeriodOptions[0]);
      } else if (tabSlug !== "resolvedFaults") {
        setSelectedTimePeriod(null);
      }
      setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    });
  };

  const handleActionClick = useCallback((battery, alertIncident) => {
    setActionDialogData({ battery, alertIncident });
    setOpenActionDialog(true);
  }, []);

  const handleIncidentsFetched = useCallback((batteryID, incidents) => {
    incidentsCacheRef.current[batteryID] = incidents;
  }, []);

  const handleSelectAllToggle = (checked) => {
    setIsManualSelectAll(true);
    setSelectAll(checked);
    if (checked) {
      setSelectedBatteries(new Set(batteryList.map((b) => b.batteryID)));
      setSelectedAlertIncidents(
        new Set(batteryList.flatMap((b) => (b.alertIncidents || []).map((i) => i.incidentID)))
      );
    } else {
      setSelectedBatteries(new Set());
      setSelectedAlertIncidents(new Set());
    }
  };

  const handleSelect = useCallback((record, selected) => {
    setSelectedBatteries((prev) => {
      const s = new Set(prev);
      selected ? s.add(record.batteryID) : s.delete(record.batteryID);
      return s;
    });
  }, []);

  const fetchBatteries = useCallback(async () => {
    if (!allDefaultFiltersApplied) return;

    const requestId = ++fetchRequestRef.current;
    setIsLoading(true);

    const searchID = searchTerm.selectedSearchColumn || "searchID";
    const dateRange =
      selectedTab === "resolvedFaults" ? getDateRangeFromTimePeriod(selectedTimePeriod) : null;

    const filters = {
      ...(dateRange && dateRange),
      [searchID]: searchTerm.selectedDevices,
      pageCount: paginationData.rowsPerPage,
      pageNum: paginationData.currentPage,
      sortBy: JSON.stringify(selectedSort),
      errorStatus: [legacyTabStatus],
      ...selectedFilters,
    };

    const res = await getDiagnosticsBatteriesList(globalFilters, filters);
    if (requestId !== fetchRequestRef.current) return;

    setBatteryList(res?.response?.data || []);
    setPaginationData((prev) => ({ ...prev, total: res?.response?.total?.total ?? prev.total }));
    setIsLoading(false);
  }, [
    allDefaultFiltersApplied,
    searchTerm,
    selectedTab,
    legacyTabStatus,
    selectedTimePeriod,
    paginationData.currentPage,
    paginationData.rowsPerPage,
    selectedSort,
    selectedFilters,
    globalFilters,
  ]);

  const handleActionDialogClose = useCallback((hasUpdatedData) => {
    setOpenActionDialog(false);
    if (hasUpdatedData) fetchBatteries();
  }, [fetchBatteries]);

  const incidentFilters = useMemo(() => {
    const dateRange = selectedTab === "resolvedFaults" ? getDateRangeFromTimePeriod(selectedTimePeriod) : null;
    const incidentsSortBy = selectedSort.map((sort) =>
      sort.sortBy === "batteriesBySeverity"
        ? { ...sort, sortBy: "severityLevelID" }
        : sort
    );
    return {
      errorStatus: legacyTabStatus,
      ...(dateRange && dateRange),
      ...selectedFilters,
      sortBy: JSON.stringify(incidentsSortBy),
      skipPagination: true,
    };
  }, [selectedTab, selectedTimePeriod, selectedFilters, selectedSort]);

  useEffect(() => {
    fetchFleets();
    fetchAlertCategories();
  }, []);

  useEffect(() => {
    if (selectedFleets.length > 0) fetchOnboardingLocations(selectedFleets);
  }, [selectedFleets]);

  useEffect(() => {
    const hasFleets = (globalFilters?.fleets || []).length > 0;
    const hasLocations = Object.keys(globalFilters?.onboardingLocations || {}).length > 0;
    // Only wait for operationalStatus if that filter is visible in the current tab config
    const hasOperationalStatus =
      !globalFilterConfig.operationalStatus ||
      (globalFilters?.operationalStatus || []).length > 0;
    setAllGlobalFiltersLoaded(hasFleets && hasLocations && hasOperationalStatus);
  }, [globalFilters, globalFilterConfig.operationalStatus]);

  useEffect(() => {
    let defaultFilters = {};
    try {
      defaultFilters = queryParams.filters ? JSON.parse(queryParams.filters) : {};
    } catch {
      defaultFilters = {};
    }
    const { originDepo, ...otherFilters } = defaultFilters;
    if (originDepo && Array.isArray(originDepo) && originDepo.length > 0 && allOnboardingLocations?.depots?.length > 0) {
      setGlobalFilters((prev) => ({
        ...prev,
        onboardingLocations: { ...prev.onboardingLocations, depots: originDepo },
      }));
    }
    setAllDefaultFiltersApplied(true);
    if (JSON.stringify(selectedFilters) !== JSON.stringify(otherFilters)) {
      setSelectedFilters(otherFilters);
    }
  }, [queryParams.filters, allOnboardingLocations]);

  useEffect(() => {
    const defaultPageSort = [{ sortBy: "batteriesBySeverity", order: "desc" }];
    if (queryParams?.sort) {
      try {
        const parsedSort = JSON.parse(queryParams.sort);
        setDefaultSort(parsedSort);
        setSelectedSort(parsedSort);
      } catch {
        setDefaultSort(defaultPageSort);
        setSelectedSort(defaultPageSort);
      }
    } else {
      setDefaultSort(defaultPageSort);
      setSelectedSort(defaultPageSort);
    }
  }, [queryParams.sort]);

  useEffect(() => {
    if (allGlobalFiltersLoaded && Object.keys(globalFilters).length > 0) {
      fetchBatteries();
    }
  }, [
    globalFilters,
    paginationData?.currentPage,
    selectedFilters,
    selectedSort,
    searchTerm,
    selectedTimePeriod,
    selectedTab,
    allDefaultFiltersApplied,
    allGlobalFiltersLoaded,
  ]);

  useEffect(() => {
    setSelectedBatteries(new Set());
    setSelectedAlertIncidents(new Set());
  }, [selectedTab]);

  useEffect(() => {
    incidentsCacheRef.current = {};
  }, [globalFilters, incidentFilters]);

  useEffect(() => {
    if (batteryList.length === 0) return;
    if (!isManualSelectAll) setSelectAll(selectedBatteries.size === batteryList.length);
    if (isManualSelectAll) setIsManualSelectAll(false);
  }, [selectedBatteries, batteryList]);

  return (
    <Box>
      {/* Header: title + tabs + global filters */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ px: 2, display: "flex" }}>
          <Typography variant="fw700s20lh24c2E2E2E">Battery System Faults</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            borderBottom: "1px solid #E4E3E3",
            mt: 0,
            pr: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AlertSystemTabs
              tabs={tabs}
              selectedTab={selectedTab}
              changeTheTab={handleTabSwitch}
            />
          </Box>
          <Grid2 container gap={1} justifyContent="center" alignItems="center">
            {selectedTab === "resolvedFaults" && (
              <SingleSelectDropdown
                selectedOption={selectedTimePeriod}
                options={timePeriodOptions}
                label="Time Period"
                onChange={handleTimePeriodSelection}
              />
            )}
            {globalFilterConfig.alertCategory && (
              <MultiSelectDropdown
                selectedOptions={globalFilters?.alertCategories ?? []}
                options={alertCategories}
                label="Alert Categories"
                onChange={handleAlertCategorySelection}
                defaultAllSelected={true}
              />
            )}
            {globalFilterConfig.operationalStatus && (
              <OperationalStatusMultiSelect
                selectedValues={globalFilters?.operationalStatus || []}
                onSelectionChange={handleOperationalStatusSelection}
                onOptionsLoaded={(allValueCodes, defaultSelection) =>
                  setGlobalFilters((prev) => ({
                    ...prev,
                    operationalStatus: defaultSelection,
                    allOperationalStatus: allValueCodes,
                  }))
                }
              />
            )}
            {globalFilterConfig.location && (
              <MultiSelectDropdown
                selectedOptions={globalFilters?.onboardingLocations?.depots || []}
                options={allOnboardingLocations?.depots || []}
                label="Operating Location"
                onChange={handleDepotSelection}
              />
            )}
          </Grid2>
        </Box>
      </Box>

      {/* Page body */}
      <Box sx={{ px: 3, py: 2, mb: 2 }}>
        <Grid2 container gap={2} flexDirection="column">
          <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {showStatistics && (
              <>
                <SeverityStatistics
                  globalFilters={globalFilters}
                  defaultFiltersApplied={allDefaultFiltersApplied}
                  globalFiltersLoaded={allGlobalFiltersLoaded}
                />
                <Divider
                  orientation="horizontal"
                  flexItem
                  sx={{ borderColor: "#E4E4E4", mx: "-24px" }}
                />
              </>
            )}

            <Grid2 container flexDirection="column" gap={2} mb={4}>
              <AlertListHeader
                onExpandToggle={(v) => setExpandAll(v)}
                onSelectAllToggle={handleSelectAllToggle}
                selectAll={selectAll}
                resultCount={paginationData.total}
                onSearch={handleSearch}
                selectedFilters={selectedFilters}
                onApplyFilters={handleFilters}
                defaultSort={defaultSort}
                onSortChange={handleSortChange}
                selectedTab={legacyTabStatus}
                selectedBatteries={selectedBatteries}
                selectedAlertIncidents={selectedAlertIncidents}
                searchTerm={searchTerm}
                globalFilters={globalFilters}
                selectedTimePeriod={selectedTimePeriod}
                incidentsCache={incidentsCacheRef.current}
                incidentFilters={incidentFilters}
                searchableFields={searchableFields}
                showEmail={false}
                showDownload={legacyTabStatus !== "resolved"}
              />

              {isLoading || !allGlobalFiltersLoaded || !allDefaultFiltersApplied ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 5.25,
                    bgcolor: "#F3F5F7",
                  }}
                >
                  <CartLoader />
                </Box>
              ) : (
                <>
                  {batteryList.length ? (
                    batteryList.map((battery) => (
                      <CustomAlertListItem
                        key={battery.batteryID}
                        battery={battery}
                        expandAll={expandAll}
                        selectedTab={legacyTabStatus}
                        selectedBatteries={selectedBatteries}
                        onSelect={handleSelect}
                        globalFilters={globalFilters}
                        incidentFilters={incidentFilters}
                        onIncidentsFetched={handleIncidentsFetched}
                      />
                    ))
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 4,
                        bgcolor: "#F3F5F7",
                      }}
                    >
                      <Typography
                        variant="fw500s12c686868"
                        sx={{ fontWeight: 600, fontSize: "18px", lineHeight: "20px" }}
                      >
                        No Vehicles found for filters applied
                      </Typography>
                    </Box>
                  )}
                  {batteryList.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "right" }}>
                      <Pagination
                        showSizeChanger={false}
                        current={paginationData.currentPage}
                        pageSize={paginationData.rowsPerPage}
                        total={paginationData?.total}
                        onChange={handlePageChange}
                        showTotal={(total, range) => `${range[0]}-${range[1]} / ${total}`}
                      />
                    </Box>
                  )}
                </>
              )}
            </Grid2>

            <AlertActionDialog
              actionData={actionDialogData}
              open={openActionDialog}
              onClose={handleActionDialogClose}
            />
            <SelectionClearWarningDialog
              open={isWarningOpen}
              onProceed={() => {
                if (pendingAction) { pendingAction(); setPendingAction(null); }
                setIsWarningOpen(false);
                clearSelection();
              }}
              onCancel={() => { setPendingAction(null); setIsWarningOpen(false); }}
            />
          </Box>
        </Grid2>
      </Box>
    </Box>
  );
}

export default CustomBMSAlertV2Main;
